/* eslint-disable */
import type { Image } from "../../cash/ui/Image.pb";
import type { Country } from "../../common/countries.pb";
import type { CurrencyCode } from "../../common/currency_codes.pb";
import type { BlockerAction } from "../blocker_action.pb";

/**
 * A blocker to present to the user a list of available instruments. An instrument can be presented
 * as Enabled or Disabled using the `state` and it's style can be controlled using `OptionStyle`.
 *
 * An EnabledState instrument can be one of three types:
 *
 * 1) `SelectInstrumentAction`: Will be used to display more information to the user before confirming his choice using `instrument_details`
 *
 * 2) `SubmitInstrumentAction`: Will usually launch a new flow (e.g. Link a new bank or card)
 *
 * 3) `ShowDialogAction`: Will usually give more context and propose additional actions as to why an instrument is ineligible
 *
 * 4) `SubmitDigitalWalletPayloadAction`: Will require client-side confirmation (ex. using the digital wallet's SDK) and client-supplied parameters
 *
 * CHANGELOG
 *
 * 3.65: Initial support (iOS/Android)
 *
 * 3.66: Support stretching DetailRow's `title` when no `detail_text` is given
 *
 * 3.68: New design for `instrument_details`
 *
 * 3.76: Added support for `Disabled` option as the preselected option
 *
 * 3.78: Support `instrument_header_text` that dynamically overrides `header_text` when an instrument is selected
 *
 * 3.79: Add new preselected_option_action with 2 options (ShowOptionsAction/ShowDialogAction)
 * 3.79: Add blocker_action to ShowDialogAction and move to ISB root for reuse
 *
 * Future: deprecate old DetailRow, then reserve the `detail_rows` field
 */
export interface InstrumentSelectionBlocker {
  /** Required. */
  header_text?: string;
  /** Required. */
  submit_button_title?: string;
  /**
   * Required (non-empty)
   * Note: opening the list of options is only available if more than one option is provided (iOS 3.72+, Android 3.71+).
   */
  options?: InstrumentSelectionBlockerInstrumentOption[];
  /**
   * The index of the option in `options` that should initially be selected.
   *
   * In Cash 3.75 and below (on both iOS and Android), the option MUST
   * be enabled.
   *
   * In Cash 3.76+ (on both Cash iOS and Android), a preselected option
   * that is disabled is allowed. The continue button is disabled until
   * the user selects an option that _is_ enabled. This is helpful for
   * scenarios where the user has no viable instrument to make a transfer,
   * like trying to move more money into Savings than the Cash Balance
   * currently holds.
   *
   * If the option is enabled, the action associated with the option MUST
   * be a `SelectInstrumentAction`, otherwise the client will treat it
   * as invalid and crash.
   *
   * If null, defaults to 0.
   */
  preselected_option_index?: number;
  show_options_action?: InstrumentSelectionBlockerShowOptionsAction | undefined;
  show_dialog_action?:
    | InstrumentSelectionBlockerShowDialogAction
    | undefined;
  /** Optional text describing the purpose of the instrument selection  (e.g. "From:") */
  selected_instrument_label?: string;
  /** Optional title to be displayed at the top of the instrument list. */
  list_header_title?: string;
  /**
   * Required.
   * Label for dismiss button in the instrument list.
   */
  list_dismiss_button_text?: string;
  /** Defaults to false if null. Temporary flag used for proof of concept. */
  use_v2_endpoint?: boolean;
}

export enum InstrumentSelectionBlockerOptionStyle {
  NORMAL = "NORMAL",
  INELIGIBLE = "INELIGIBLE",
}

/**
 * The ShowDialogAction can be triggered from the preselected option OR by tapping on an option in the list
 * It will be displayed with an optional button controlled by the presence of the submit_button_text.
 * If the customer taps the submit button, the client will submit the blocker immediately if
 * blocker_action is null or will otherwise perform the specified blocker_action
 */
export interface InstrumentSelectionBlockerShowDialogAction {
  /** Required */
  title?: string;
  body?: string;
  /** Label for an optional submit button */
  submit_button_text?: string;
  /** Defaults to SubmitAction if null */
  blocker_action?: BlockerAction;
  /**
   * Required
   * Label for a dismiss button
   */
  dismiss_button_text?: string;
}

/** Default behavior to display a list of all available options */
export interface InstrumentSelectionBlockerShowOptionsAction {
}

export interface InstrumentSelectionBlockerInstrumentOption {
  /**
   * The avatar to show for this option, typically the bank’s logo. When null, the
   * client will show a fallback icon based on the `icon_style`.
   */
  avatar?: Image;
  new_link_icon?: InstrumentSelectionBlockerInstrumentOptionNewLinkIcon | undefined;
  card_icon?: InstrumentSelectionBlockerInstrumentOptionCardIcon | undefined;
  bank_icon?: InstrumentSelectionBlockerInstrumentOptionBankIcon | undefined;
  balance_icon?: InstrumentSelectionBlockerInstrumentOptionBalanceIcon | undefined;
  app_icon?:
    | InstrumentSelectionBlockerInstrumentOptionAppIcon
    | undefined;
  /**
   * Required.
   * The styling used for this option. For example, an ineligible option
   * may be rendered as greyed out.
   */
  option_style?: InstrumentSelectionBlockerOptionStyle;
  /**
   * Required.
   * Title used when shown in the instrument list.
   */
  list_title?: string;
  /**
   * Subtitle used when shown in the instrument list. This may be truncated
   * in favor of displaying the `list_subtitle_suffix`, if provided.
   */
  list_subtitle_primary?: string;
  /** Subtitle suffix used when shown in the instrument list. */
  list_subtitle_suffix?: string;
  /** Label for an optional inline pill when shown in the instrument list. */
  list_pill_text?: string;
  /**
   * Required.
   * Textual representation of all the instrument's details
   */
  list_accessibility_label?: string;
  enabled_state?: InstrumentSelectionBlockerInstrumentOptionEnabledState | undefined;
  disabled_state?: InstrumentSelectionBlockerInstrumentOptionDisabledState | undefined;
}

export interface InstrumentSelectionBlockerInstrumentOptionNewLinkIcon {
}

export interface InstrumentSelectionBlockerInstrumentOptionCardIcon {
}

export interface InstrumentSelectionBlockerInstrumentOptionBankIcon {
}

export interface InstrumentSelectionBlockerInstrumentOptionAppIcon {
}

export interface InstrumentSelectionBlockerInstrumentOptionBalanceIcon {
  currency_code?: CurrencyCode;
}

/** The instrument option can be submitted to the blocker. */
export interface InstrumentSelectionBlockerInstrumentOptionEnabledState {
  /**
   * Required.
   * The encoded InstrumentSelectionOption proto.
   * This is intended to be opaque to the client and is used to send back to
   * server in SelectInstrumentRequest.encoded_selected_option on selection.
   */
  encoded_selected_option?: Uint8Array;
  submit_instrument_action?: InstrumentSelectionBlockerInstrumentOptionEnabledStateSubmitInstrumentAction | undefined;
  select_instrument_action?: InstrumentSelectionBlockerInstrumentOptionEnabledStateSelectInstrumentAction | undefined;
  show_dialog_action?: InstrumentSelectionBlockerShowDialogAction | undefined;
  submit_digital_wallet_payload_action?:
    | InstrumentSelectionBlockerInstrumentOptionEnabledStateSubmitDigitalWalletPayloadAction
    | undefined;
}

/** On selection, submit this option to the blocker immediately. */
export interface InstrumentSelectionBlockerInstrumentOptionEnabledStateSubmitInstrumentAction {
}

/**
 * On selection, confirm usage of the digital wallet represented by
 * the option (ex. by using the Apple Pay SDK) and immediately submit the option
 * plus any additional payload (ex. required Apple Pay params) if successful.
 */
export interface InstrumentSelectionBlockerInstrumentOptionEnabledStateSubmitDigitalWalletPayloadAction {
  apple_pay_wallet?:
    | InstrumentSelectionBlockerInstrumentOptionEnabledStateSubmitDigitalWalletPayloadActionApplePayWallet
    | undefined;
  google_pay_wallet?:
    | InstrumentSelectionBlockerInstrumentOptionEnabledStateSubmitDigitalWalletPayloadActionGooglePayWallet
    | undefined;
}

export interface InstrumentSelectionBlockerInstrumentOptionEnabledStateSubmitDigitalWalletPayloadActionApplePayWallet {
}

export interface InstrumentSelectionBlockerInstrumentOptionEnabledStateSubmitDigitalWalletPayloadActionGooglePayWallet {
  /**
   * Required
   * Used to send allowedCardNetworks in CardParameters by the client -
   * https://developers.google.com/pay/api/android/reference/request-objects#CardParameters
   */
  list_allowed_card_network?:
    InstrumentSelectionBlockerInstrumentOptionEnabledStateSubmitDigitalWalletPayloadActionGooglePayWalletCardNetwork[];
  /**
   * Required
   * Used to send allowPrepaidCards in CardParameters by the client -
   * https://developers.google.com/pay/api/android/reference/request-objects#CardParameters
   */
  allow_prepaid_cards?: boolean;
  /**
   * Required
   * Used to send allowCreditCards in CardParameters by the client -
   * https://developers.google.com/pay/api/android/reference/request-objects#CardParameters
   */
  allow_credit_cards?: boolean;
  /**
   * Required
   * Used to send existingPaymentMethodRequired in IsReadyToPayRequest by the client -
   * https://developers.google.com/pay/api/android/reference/request-objects#IsReadyToPayRequest
   */
  existing_payment_method_required?: boolean;
  /**
   * Required
   * Used to send allowedAuthMethods in CardParameters by the client -
   * https://developers.google.com/pay/api/android/reference/request-objects#CardParameters. We will use only CRYPTOGRAM_3DS to begin with since we don’t want to perform additional 3DS checks on our side.
   */
  supported_auth_methods?:
    InstrumentSelectionBlockerInstrumentOptionEnabledStateSubmitDigitalWalletPayloadActionGooglePayWalletAuthMethod[];
  /**
   * Required
   * Used to send gateway and gatewayMerchantId in TokenSpecification by the client -
   * https://developers.google.com/pay/api/android/reference/request-objects#gateway
   */
  tokenizationParameters?:
    InstrumentSelectionBlockerInstrumentOptionEnabledStateSubmitDigitalWalletPayloadActionGooglePayWalletTokenizationParameters;
  /**
   * Required
   * Used to send merchantName in MerchantInfo by the client -
   * https://developers.google.com/pay/api/android/reference/request-objects#MerchantInfo
   */
  merchant_name?: string;
  /**
   * Required for EU/Optional for UK
   * Used to send countryCode in TransactionInfo by the client -
   * https://developers.google.com/pay/api/android/reference/request-objects#TransactionInfo
   */
  acquirer_bank_country_code?: Country;
}

export enum InstrumentSelectionBlockerInstrumentOptionEnabledStateSubmitDigitalWalletPayloadActionGooglePayWalletCardNetwork {
  AMEX = "AMEX",
  DISCOVER = "DISCOVER",
  INTERAC = "INTERAC",
  JCB = "JCB",
  MASTERCARD = "MASTERCARD",
  VISA = "VISA",
}

export enum InstrumentSelectionBlockerInstrumentOptionEnabledStateSubmitDigitalWalletPayloadActionGooglePayWalletAuthMethod {
  CRYPTOGRAM_3DS = "CRYPTOGRAM_3DS",
}

export interface InstrumentSelectionBlockerInstrumentOptionEnabledStateSubmitDigitalWalletPayloadActionGooglePayWalletTokenizationParameters {
  /**
   * Required
   * This string will be "square"
   */
  gateway?: string;
  /**
   * Required
   * A constant merchant id for Cash
   */
  gatewayMerchantId?: string;
}

/**
 * On selection, display the option as selected but do not submit
 * immediately. The selection can later be submitted via the main submit
 * button.
 */
export interface InstrumentSelectionBlockerInstrumentOptionEnabledStateSelectInstrumentAction {
  /**
   * Optional (could be empty)
   * Deprecated starting with 3.68+ (inclusive) - Use instrument_details instead.
   *
   * @deprecated
   */
  detail_rows?: InstrumentSelectionBlockerInstrumentOptionEnabledStateSelectInstrumentActionDetailRow[];
  /**
   * Required.
   * Title displayed in the picker when selected.
   */
  selected_title?: string;
  /**
   * Subtitle displayed in the picker when selected. This may be truncated
   * in favor of displaying the `selected_subtitle_suffix`, if provided.
   * For existing instruments, the brand or type of the instrument, e.g. "Visa"
   * for a card or "Checking" for a bank account. For new instruments, this is
   * the speed.
   */
  selected_subtitle_primary?: string;
  /**
   * The suffix of the card/account number for existing instruments,
   * or null for new instruments.
   */
  selected_subtitle_suffix?: string;
  /**
   * Required.
   * Textual representation of all the selected instrument's details
   */
  selected_accessibility_label?: string;
  detail?: InstrumentSelectionBlockerInstrumentOptionEnabledStateSelectInstrumentActionDetail | undefined;
  detail_list?:
    | InstrumentSelectionBlockerInstrumentOptionEnabledStateSelectInstrumentActionDetailList
    | undefined;
  /**
   * Starting with 3.78 (inclusive) you can update the title of the blocker
   * when an instrument is selected. If provided, this will override header_text.
   */
  instrument_header_text?: string;
}

/** Will be deprecated soon after 3.68 */
export interface InstrumentSelectionBlockerInstrumentOptionEnabledStateSelectInstrumentActionDetailRow {
  /**
   * Required.
   * The title, shown at the left of the row. Supports Markdown.
   *
   * @deprecated
   */
  title?: string;
  /**
   * Required.
   * The detail text, shown at the right of the row. Supports Markdown.
   * Sending empty will allow title to take full screen width (3.66 and 3.67)
   *
   * @deprecated
   */
  detail_text?: string;
  /**
   * If non-null, an accessory will be shown next to the title and
   * tapping the `DetailRow` will present a dialog populated with this text.
   *
   * @deprecated
   */
  info_dialog_text?: string;
  /**
   * If there is an info dialog (i.e. info_dialog_text != null),
   * this is required to show a dismiss button in the dialog.
   *
   * @deprecated
   */
  info_dialog_dismiss_button_text?: string;
}

export interface InstrumentSelectionBlockerInstrumentOptionEnabledStateSelectInstrumentActionDetail {
  /** Supports Markdown. */
  text?: string;
  dialog?: InstrumentSelectionBlockerInstrumentOptionEnabledStateSelectInstrumentActionInstrumentDetailsDialog;
}

export interface InstrumentSelectionBlockerInstrumentOptionEnabledStateSelectInstrumentActionDetailList {
  /** Optional (could be empty) */
  detail_rows?: InstrumentSelectionBlockerInstrumentOptionEnabledStateSelectInstrumentActionDetailListDetailRow[];
}

export interface InstrumentSelectionBlockerInstrumentOptionEnabledStateSelectInstrumentActionDetailListDetailRow {
  /**
   * Required.
   * The label, shown at the left of the row. Supports Markdown.
   */
  label?: string;
  /**
   * Required.
   * The value, shown at the right of the row. Supports Markdown.
   */
  value?: string;
  dialog?: InstrumentSelectionBlockerInstrumentOptionEnabledStateSelectInstrumentActionInstrumentDetailsDialog;
}

export interface InstrumentSelectionBlockerInstrumentOptionEnabledStateSelectInstrumentActionInstrumentDetailsDialog {
  /**
   * If non-null, an accessory will be shown next to the title and
   * tapping the `DetailRow` will present a dialog populated with this text.
   */
  info_dialog_text?: string;
  /**
   * If there is an info dialog (i.e. info_dialog_text != null),
   * this is required to show a dismiss button in the dialog.
   */
  info_dialog_dismiss_button_text?: string;
}

/**
 * The instrument option cannot be selected to be submitted to the blocker.
 * It can, however, be the preselected option (see preselected_option_index), which should make
 * the blocker unsubmittable until a different option is chosen.
 */
export interface InstrumentSelectionBlockerInstrumentOptionDisabledState {
  /**
   * Title displayed in the picker when selected.
   * Can only happen when this is the preselected option in the blocker.
   */
  selected_title?: string;
  /**
   * Subtitle displayed in the picker when selected. This may be truncated
   * in favor of displaying the `selected_subtitle_suffix`, if provided.
   * For existing instruments, the brand or type of the instrument, e.g. "Visa"
   * for a card or "Checking" for a bank account. For new instruments, this is
   * the speed.
   */
  selected_subtitle_primary?: string;
  /**
   * The suffix of the card/account number for existing instruments,
   * or null for new instruments.
   */
  selected_subtitle_suffix?: string;
}
